import _ from 'lodash'
import { fork, select, take, call, put, takeEvery, delay } from 'redux-saga/effects'
import { Auth } from '../../amplify'
import requestWithRetry from 'civic-champs-shared/api/requestWithRetry'
import { safeHubSpotCall } from 'civic-champs-shared/utils/useChatWidget'

import parseUsername from 'auth/utils/parseUsername'
import { userSwitchFinished, userSwitchStarted } from '../../auth/actions'
import { clearCognitoKeys } from 'civic-champs-shared/auth/utils/cognito'
import { isFeatureEnabled, isFeaturesAvailable } from '../../core/feature/selectors'
import {
  START_KIOSK,
  STOP_KIOSK,
  ABANDON_KIOSK,
  START_KIOSK_CLEAR_FAILURE,
  startKioskSucceeded,
  startKioskFailed,
  kioskStarted,
  stopKioskSucceeded,
  stopKioskFailed,
  kioskStopped,
} from '../actions'
import { getOpportunity, getEncodedOccurrence, isKioskMode } from '../selectors'
import {
  KIOSK_SESSION_LIFECYCLE_FEATURE_KEY,
  isKioskSessionLifecycleEnabledFromPersist,
} from '../kioskSessionLifecycleFeature'
import { encodeOccurrenceFromEvent } from '../../Event/helpers/encodeOccurrence'
import { reportSessionStopped } from '../kioskDiagnostics'
import { requestSetFullscreen } from '../fullscreen'
import warnOnUnload from '../warnOnUnload'

function stopNotificationStream() {
  if (window.sharedWorker) {
    window.sharedWorker.port.postMessage({ event: 'stop' })
  }
}

export default function* kioskModeSaga() {
  yield fork(kioskModeFlow)
  yield fork(watchClearFailure)
}

export function* kioskModeFlow() {
  // Prod path when KioskSessionLifecycle is off: start from rehydrated kiosk state immediately.
  let running = yield select(isKioskMode)

  if (isKioskSessionLifecycleEnabledFromPersist()) {
    if (!(yield select(isFeaturesAvailable))) {
      yield call(waitForFeaturesAvailable)
    }
    if (yield call(isKioskSessionLifecycleEnabled)) {
      yield call(clearOrphanedKioskSession)
      running = yield call(resumeKioskIfPersisted)
    }
  } else if (yield select(isFeaturesAvailable)) {
    // Switch enabled in this session but not yet in cached persist (e.g. toggled without reload).
    if (yield call(isKioskSessionLifecycleEnabled)) {
      yield call(clearOrphanedKioskSession)
      running = yield call(resumeKioskIfPersisted)
    }
  }

  while (true) {
    while (!running) {
      const startAction = yield take(START_KIOSK)
      running = yield call(tryStartKiosk, startAction)
    }

    yield put(kioskStarted())

    while (running) {
      const stopAction = yield take([STOP_KIOSK, ABANDON_KIOSK])
      if (stopAction.type === ABANDON_KIOSK) {
        running = yield call(abandonKioskSession)
      } else {
        running = yield call(tryStopKiosk, stopAction)
      }
    }

    yield put(kioskStopped())
  }
}

function* waitForFeaturesAvailable() {
  while (!(yield select(isFeaturesAvailable))) {
    yield delay(100)
  }
}

function* isKioskSessionLifecycleEnabled() {
  return yield select(state => isFeatureEnabled(state, KIOSK_SESSION_LIFECYCLE_FEATURE_KEY))
}

function isKioskAuthenticatedUser(user) {
  try {
    const permissions = JSON.parse(user?.attributes?.['custom:permissions'] || '{}')
    return permissions?.role === 'KIOSK'
  } catch (e) {
    return false
  }
}

export function* signOutKioskCredentials() {
  clearCognitoKeys()
  localStorage.removeItem('token')
  localStorage.removeItem('firstLogin')
  try {
    yield call([Auth, 'signOut'])
  } catch (e) {
    // signOut can fail if the session is already gone.
  }
}

// Only runs when KioskSessionLifecycle is enabled. Cognito kiosk sessions can outlive
// redux kiosk state; sign out so admin does not load under a kiosk user.
export function* clearOrphanedKioskSession() {
  if (yield select(isKioskMode)) {
    return
  }

  try {
    const user = yield call([Auth, 'currentAuthenticatedUser'])
    if (!isKioskAuthenticatedUser(user)) {
      return
    }
  } catch (e) {
    return
  }

  stopNotificationStream()
  warnOnUnload.stop()
  yield put(requestSetFullscreen(false))
  yield call(safeHubSpotCall, 'load')
  yield call(signOutKioskCredentials)
}

export function* resumeKioskIfPersisted() {
  if (!(yield select(isKioskMode))) {
    return false
  }

  const opportunity = yield select(getOpportunity)
  if (!opportunity) {
    yield put(stopKioskSucceeded())
    return false
  }

  yield put(userSwitchStarted())
  try {
    warnOnUnload.start()
    yield call(safeHubSpotCall, 'remove')
    yield put(requestSetFullscreen(true))
    stopNotificationStream()

    let hasKioskSession = false
    try {
      const user = yield call([Auth, 'currentAuthenticatedUser'])
      hasKioskSession = isKioskAuthenticatedUser(user)
    } catch (e) {
      hasKioskSession = false
    }

    if (!hasKioskSession) {
      yield call(loginKioskUser, opportunity)
    }

    yield put(startKioskSucceeded())
    return true
  } catch (e) {
    warnOnUnload.stop()
    yield call(safeHubSpotCall, 'load')
    yield put(startKioskFailed(e))
    return false
  } finally {
    yield put(userSwitchFinished())
  }
}

export function* tryStartKiosk(startAction) {
  yield put(userSwitchStarted())
  try {
    warnOnUnload.start()
    yield call(safeHubSpotCall, 'remove')
    yield put(requestSetFullscreen(true)) //TODO must be in response to user input
    yield call(loginKioskUser, _.get(startAction, 'payload.opportunity'))
    stopNotificationStream()
    yield put(startKioskSucceeded())
    return true
  } catch (e) {
    warnOnUnload.stop()
    yield call(safeHubSpotCall, 'load')
    yield put(startKioskFailed(e))
    return false
  } finally {
    yield put(userSwitchFinished())
  }
}

export function* tryStopKiosk(stopAction) {
  const auditContext = yield call(selectKioskAuditContext)

  if (!(yield call(exitToAdminSession, stopAction))) {
    return true
  }

  // Audited after the switch so the admin app is already on screen while these run.
  yield call(recordKioskStop, 'explicit_stop', auditContext)
  yield call(reportKioskSessionStopped, 'explicit_stop', auditContext)
  return false
}

function* exitToAdminSession(stopAction) {
  yield put(userSwitchStarted())
  try {
    yield call(logoutKioskUser, stopAction)
    yield put(requestSetFullscreen(false))
    warnOnUnload.stop()
    yield put(stopKioskSucceeded())
    return true
  } catch (e) {
    yield put(stopKioskFailed(e))
    return false
  } finally {
    yield put(userSwitchFinished())
  }
}

export function* abandonKioskSession() {
  const auditContext = yield call(selectKioskAuditContext)

  stopNotificationStream()
  warnOnUnload.stop()
  yield put(requestSetFullscreen(false))
  yield call(safeHubSpotCall, 'load')
  yield call(recordKioskStop, 'abandon', auditContext)
  yield call(reportKioskSessionStopped, 'abandon', auditContext)
  yield call(signOutKioskCredentials)
  return false
}

function* selectKioskAuditContext() {
  return {
    organizationId: yield select(state => _.get(state, 'kiosk.opportunity.organization.id')),
    encodedOccurrence: yield select(getEncodedOccurrence),
  }
}

export function* recordKioskStop(reason, { organizationId, encodedOccurrence }) {
  if (!organizationId || !encodedOccurrence) {
    return
  }

  try {
    yield call(requestWithRetry, {
      method: 'post',
      url: `/organizations/${organizationId}/kiosk/stop`,
      data: { encodedOccurrence, reason },
    })
  } catch (e) {
    // Audit logging must not block kiosk exit.
  }
}

export function* reportKioskSessionStopped(reason, context) {
  if (!context.organizationId || !context.encodedOccurrence) {
    return
  }

  yield call(reportSessionStopped, context, reason)
}

export function* fetchKioskCredentials(opportunity) {
  const id = _.get(opportunity, 'organization.id')
  const encodedOccurrence = encodeOccurrenceFromEvent(opportunity)
  const url = `/organizations/${id}/kiosk`
  const data = encodedOccurrence ? { encodedOccurrence } : undefined
  return yield call(requestWithRetry, { method: 'post', url, data })
}

// Amplify v6 rejects signIn while anybody is signed in (UserAlreadyAuthenticatedException),
// so switching between the admin and the kiosk user always needs an explicit sign out first.
export function* signInAs(credentials) {
  yield call(signOutKioskCredentials)
  yield call([Auth, 'signIn'], credentials.username, credentials.password)
}

export function* loginKioskUser(opportunity) {
  const credentials = yield call(fetchKioskCredentials, opportunity)
  yield call(signInAs, credentials)
}

export function* logoutKioskUser(endEvent) {
  const { username, password } = _.get(endEvent, 'payload.adminCredentials')
  const opportunity = yield select(getOpportunity)
  // Signing out is now a prerequisite of the admin sign in, so a mistyped password would
  // otherwise strand an unattended kiosk. Credentials are fetched per exit attempt and kept
  // in memory only, so nothing extra is stored on the device.
  const kioskCredentials = yield call(fetchKioskCredentials, opportunity)

  try {
    yield call(signInAs, { username: parseUsername(username), password })
  } catch (error) {
    yield call(restoreKioskSession, kioskCredentials)
    throw error
  }
}

function* restoreKioskSession(credentials) {
  try {
    yield call(signInAs, credentials)
  } catch (error) {
    // Nothing left to fall back to; tryStopKiosk reports the original admin sign-in error.
  }
}

export function* watchClearFailure() {
  yield takeEvery(START_KIOSK_CLEAR_FAILURE, function* () {
    yield put(requestSetFullscreen(false))
  })
}
